/**
 * Phase 2.14 — Telecom Vendor Exposure (presentation-only).
 *
 * Risk-based, neutral framing. CSV Pro captures vendor exposure and
 * dependency information to support executive, regulatory, and risk-based
 * decision-making. CSV Pro does not independently designate vendors as
 * restricted or prohibited.
 */

export interface VendorExposureRow {
  id: string;
  vendor_name: string;
  vendor_type: string | null;
  /** Legacy single-domain column (kept in sync server-side). */
  telecom_domain: string | null;
  /** Multi-domain selection (new canonical field). */
  telecom_domains: string[] | null;
  deployment_scope: string | null;
  dependency_level: string | null;
  estimated_percentage: number | null;
  high_risk_flag: string | null;
  evidence_source: string | null;
  reviewer_notes: string | null;
  /** Optional reviewer-written override for the auto disruption impact text. */
  disruption_impact_override?: string | null;
}


export const VENDOR_TYPE_OPTIONS = [
  { value: "oem", label: "OEM" },
  { value: "cloud_provider", label: "Cloud Provider" },
  { value: "msp", label: "Managed Service Provider" },
  { value: "software_provider", label: "Software Provider" },
  { value: "systems_integrator", label: "Systems Integrator" },
  { value: "other", label: "Other" },
] as const;

export const TELECOM_DOMAIN_OPTIONS = [
  { value: "ran", label: "RAN" },
  { value: "core", label: "Core Network" },
  { value: "transport", label: "Transport" },
  { value: "oss_bss", label: "OSS / BSS" },
  { value: "cloud", label: "Cloud" },
  { value: "data_center", label: "Data Center" },
  { value: "cybersecurity", label: "Cybersecurity" },
  { value: "customer_systems", label: "Customer Systems" },
  { value: "other", label: "Other" },
] as const;

export const DEPLOYMENT_SCOPE_OPTIONS = [
  { value: "national", label: "National" },
  { value: "regional", label: "Regional" },
  { value: "limited", label: "Limited" },
  { value: "pilot", label: "Pilot" },
  { value: "legacy", label: "Legacy" },
  { value: "unknown", label: "Unknown" },
] as const;

export const DEPENDENCY_LEVEL_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
  { value: "unknown", label: "Unknown" },
] as const;

export const HIGH_RISK_FLAG_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Unknown" },
] as const;

export const EVIDENCE_SOURCE_OPTIONS = [
  { value: "questionnaire", label: "Questionnaire" },
  { value: "documentation", label: "Documentation" },
  { value: "interview", label: "Interview" },
  { value: "reviewer_input", label: "Reviewer Input" },
] as const;

function labelFromOptions(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null | undefined,
): string {
  if (!value) return "—";
  const match = options.find((o) => o.value === value);
  return match ? match.label : value;
}

export function vendorTypeLabel(v: string | null | undefined): string {
  return labelFromOptions(VENDOR_TYPE_OPTIONS, v);
}
export function telecomDomainLabel(v: string | null | undefined): string {
  return labelFromOptions(TELECOM_DOMAIN_OPTIONS, v);
}
export function deploymentScopeLabel(v: string | null | undefined): string {
  return labelFromOptions(DEPLOYMENT_SCOPE_OPTIONS, v);
}
export function dependencyLevelLabel(v: string | null | undefined): string {
  return labelFromOptions(DEPENDENCY_LEVEL_OPTIONS, v);
}
export function highRiskFlagLabel(v: string | null | undefined): string {
  return labelFromOptions(HIGH_RISK_FLAG_OPTIONS, v);
}
export function evidenceSourceLabel(v: string | null | undefined): string {
  return labelFromOptions(EVIDENCE_SOURCE_OPTIONS, v);
}

/**
 * Resolve the canonical list of selected domain values for a vendor.
 * Prefers the new `telecom_domains` array; falls back to the legacy
 * single `telecom_domain` column so older rows still render correctly.
 */
export function resolveVendorDomains(v: VendorExposureRow): string[] {
  const arr = Array.isArray(v.telecom_domains)
    ? v.telecom_domains.filter((d): d is string => typeof d === "string" && d.trim().length > 0)
    : [];
  if (arr.length > 0) return arr;
  if (v.telecom_domain && v.telecom_domain.trim().length > 0) return [v.telecom_domain];
  return [];
}

/** Multi-domain display label joined by " / " on one line. */
export function telecomDomainsLabel(values: string[] | null | undefined): string {
  const arr = Array.isArray(values) ? values.filter(Boolean) : [];
  if (arr.length === 0) return "—";
  return arr.map((d) => telecomDomainLabel(d)).join(" / ");
}

export const VENDOR_EXPOSURE_DISCLAIMER =
  "This section provides executive visibility into the technology vendors and third-party providers underpinning the organisation's telecom and IT estate. It is intended to support strategic, regulatory, and risk-based decision-making by surfacing concentration, dependency, and sourcing exposure. Vendors are presented for informational purposes only and are not independently designated as restricted or prohibited.";

/**
 * Disruption Impact — deterministic, ≤10 words per vendor.
 *
 * Hybrid composition:
 *   1. If the reviewer provided `disruption_impact_override`, return it verbatim.
 *   2. Otherwise compose `{vendor role in dominant domain} — {severity clause}.`
 *      Vendor role comes from a curated `(vendor, domain)` lookup, falling
 *      back to a generic per-domain role. Severity clause comes from
 *      `(deployment_scope, dependency_level)`. The result is naturally
 *      unique per vendor/domain/scope/dependency combination.
 */

/** Lower-cased vendor name → domain → short role phrase. */
const VENDOR_DOMAIN_ROLE: Record<string, Partial<Record<string, string>>> = {
  cisco:        { data_center: "core routing & switching fabric", transport: "IP/MPLS transport backbone", core: "IP core & signaling routing", cybersecurity: "network security & firewalling" },
  juniper:      { transport: "IP/MPLS transport backbone", core: "IP core routing", data_center: "data center switching fabric" },
  huawei:       { ran: "radio access equipment", core: "mobile core network", transport: "optical & IP transport" },
  ericsson:     { ran: "radio access equipment", core: "mobile core network", oss_bss: "OSS service orchestration" },
  nokia:        { ran: "radio access equipment", core: "mobile core network", transport: "optical transport" },
  zte:          { ran: "radio access equipment", core: "mobile core network", transport: "optical & IP transport" },
  samsung:      { ran: "radio access equipment" },
  vmware:       { data_center: "virtualization & hypervisor layer", cloud: "private cloud platform" },
  "red hat":    { data_center: "Linux & OpenShift workload platform", cloud: "container orchestration platform" },
  redhat:       { data_center: "Linux & OpenShift workload platform", cloud: "container orchestration platform" },
  microsoft:    { cloud: "Azure cloud workloads", oss_bss: "productivity & identity services", customer_systems: "customer-facing software stack" },
  aws:          { cloud: "AWS cloud workloads", data_center: "cloud hosting infrastructure" },
  "amazon web services": { cloud: "AWS cloud workloads" },
  google:       { cloud: "GCP cloud workloads" },
  oracle:       { oss_bss: "billing & ERP platform", data_center: "database platform" },
  sap:          { oss_bss: "ERP & business systems" },
  salesforce:   { customer_systems: "CRM & customer engagement" },
  ibm:          { data_center: "mainframe & enterprise computing", oss_bss: "enterprise software platform" },
  dell:         { data_center: "server & storage infrastructure" },
  hpe:          { data_center: "server & storage infrastructure" },
  netapp:       { data_center: "enterprise storage platform" },
  fortinet:     { cybersecurity: "network security & firewalling" },
  "palo alto":  { cybersecurity: "network security & firewalling" },
  "palo alto networks": { cybersecurity: "network security & firewalling" },
  crowdstrike:  { cybersecurity: "endpoint detection & response" },
  netscout:     { cybersecurity: "network visibility & monitoring" },
};

const DOMAIN_GENERIC_ROLE: Record<string, string> = {
  ran: "radio access services",
  core: "core network services",
  transport: "transport network services",
  oss_bss: "OSS/BSS operations",
  cloud: "cloud workloads",
  data_center: "data center infrastructure",
  cybersecurity: "cybersecurity operations",
  customer_systems: "customer-facing systems",
  other: "supporting services",
};

const DOMAIN_PRIORITY = ["ran", "core", "transport", "oss_bss", "data_center", "cloud", "cybersecurity", "customer_systems", "other"];

function normalizeDependency(v: string | null | undefined): "critical" | "high" | "medium" | "low" {
  const x = (v ?? "").toLowerCase();
  if (x === "critical") return "critical";
  if (x === "high") return "high";
  if (x === "medium") return "medium";
  return "low";
}

function severityClause(
  scope: string | null | undefined,
  dep: "critical" | "high" | "medium" | "low",
): string {
  const s = (scope ?? "").toLowerCase();
  if (dep === "critical" && s === "national") return "nationwide outage risk";
  if (dep === "critical" && s === "regional") return "regional outage risk";
  if (dep === "critical")                     return "severe service disruption risk";
  if (dep === "high" && s === "national")     return "broad service degradation";
  if (dep === "high" && s === "regional")     return "regional service degradation";
  if (dep === "high")                         return "significant service degradation";
  if (dep === "medium" && (s === "limited" || s === "pilot")) return "localized disruption";
  if (dep === "medium")                       return "moderate operational disruption";
  if (s === "pilot" || s === "legacy")        return "contained impact";
  return "limited operational impact";
}

function rolePhrase(vendorName: string, domain: string): string {
  const key = (vendorName ?? "").trim().toLowerCase();
  const map = VENDOR_DOMAIN_ROLE[key];
  const specific = map ? map[domain] : undefined;
  return specific ?? DOMAIN_GENERIC_ROLE[domain] ?? DOMAIN_GENERIC_ROLE.other;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

export function buildDisruptionImpact(vendor: VendorExposureRow): string {
  const override = (vendor.disruption_impact_override ?? "").trim();
  if (override.length > 0) return override;

  const dep = normalizeDependency(vendor.dependency_level);
  const domains = resolveVendorDomains(vendor);
  const ordered = [...domains].sort(
    (a, b) => DOMAIN_PRIORITY.indexOf(a) - DOMAIN_PRIORITY.indexOf(b),
  );
  const primary = ordered[0] ?? "other";
  const role = rolePhrase(vendor.vendor_name ?? "", primary);
  const sev = severityClause(vendor.deployment_scope, dep);
  return `${capitalize(role)} — ${sev}.`;
}

/**
 * Short, neutral narrative paragraph for the Executive Summary.
 * Returns null when there are no vendors, so the Executive Summary
 * omits the section entirely (per Phase 2.14 requirement).
 */
export function buildVendorExposureNarrative(
  vendors: VendorExposureRow[],
): string | null {
  if (!Array.isArray(vendors) || vendors.length === 0) return null;

  const total = vendors.length;
  const critical = vendors.filter((v) => v.dependency_level === "critical").length;
  const high = vendors.filter((v) => v.dependency_level === "high").length;
  const highRisk = vendors.filter((v) => v.high_risk_flag === "yes").length;

  const parts: string[] = [];
  parts.push(
    `The reviewer captured ${total} vendor${total === 1 ? "" : "s"} supporting the organization's telecommunications operations.`,
  );
  if (critical > 0 || high > 0) {
    const segs: string[] = [];
    if (critical > 0) segs.push(`${critical} flagged as critical dependency`);
    if (high > 0) segs.push(`${high} as high dependency`);
    parts.push(`Of these, ${segs.join(" and ")}.`);
  }
  if (highRisk > 0) {
    parts.push(
      `${highRisk} vendor${highRisk === 1 ? " was" : "s were"} marked as high-risk for executive consideration.`,
    );
  }
  parts.push(VENDOR_EXPOSURE_DISCLAIMER);
  return parts.join(" ");
}

export interface VendorExposureTableRow {
  vendor_name: string;
  vendor_type: string;
  telecom_domain: string;
  deployment_scope: string;
  dependency_level: string;
  estimated_percentage: string;
  high_risk_flag: string;
  evidence_source: string;
  reviewer_notes: string;
  disruption_impact: string;
}

export function buildVendorExposureTableRows(
  vendors: VendorExposureRow[],
): VendorExposureTableRow[] {
  return (vendors ?? []).map((v) => ({
    vendor_name: v.vendor_name ?? "—",
    vendor_type: vendorTypeLabel(v.vendor_type),
    telecom_domain: telecomDomainsLabel(resolveVendorDomains(v)),
    deployment_scope: deploymentScopeLabel(v.deployment_scope),
    dependency_level: dependencyLevelLabel(v.dependency_level),
    estimated_percentage:
      v.estimated_percentage != null && Number.isFinite(Number(v.estimated_percentage))
        ? `${Number(v.estimated_percentage).toFixed(0)}%`
        : "—",
    high_risk_flag: highRiskFlagLabel(v.high_risk_flag),
    evidence_source: evidenceSourceLabel(v.evidence_source),
    reviewer_notes: (v.reviewer_notes ?? "").trim() || "—",
    disruption_impact: buildDisruptionImpact(v),
  }));
}
